import { Text } from "@src/lib/ui-react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { useGetAllEvidenceDefinitions } from "@src/appV2/redesign/Evidence/api/useGetAllEvidenceDefinitions";
import { DynamicSchemaForm } from "@src/appV2/redesign/Evidence/components/DynamicSchemaForm";
import { EvidenceDefinitionSelect } from "@src/appV2/redesign/Evidence/components/EvidenceDefinitionSelect";
import { toJsonSchema } from "@src/appV2/redesign/Evidence/types";
import { Divider } from "antd";
import { useMemo } from "react";
import { type Control, type FieldErrors, type FieldValues } from "react-hook-form";

import { type EvidenceData } from "./useGetDocumentEvidences";

interface DocumentEvidenceSectionProps {
  evidence: EvidenceData;
  selectedDefinitionId: string;
  onDefinitionChange: (definitionId: string) => void;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
}

export function DocumentEvidenceSection({
  evidence,
  selectedDefinitionId,
  onDefinitionChange,
  control,
  errors,
}: DocumentEvidenceSectionProps) {
  const {
    definitions,
    isLoading: isLoadingDefinitions,
    isError: isDefinitionsError,
  } = useGetAllEvidenceDefinitions();

  const evidenceDefinitionId = evidence.relationships.evidenceDefinition.data.id;

  const selectedDefinition = useMemo(
    () => definitions.find((d) => d.id === selectedDefinitionId),
    [definitions, selectedDefinitionId]
  );

  const definitionMissing =
    !isLoadingDefinitions &&
    !isDefinitionsError &&
    definitions.length > 0 &&
    !definitions.some((d) => d.id === evidenceDefinitionId);

  const propertySchema = selectedDefinition
    ? toJsonSchema(selectedDefinition.attributes.propertySchema)
    : null;
  const hasProperties = propertySchema
    ? Object.keys(propertySchema.properties ?? {}).length > 0
    : false;

  if (isLoadingDefinitions) {
    return (
      <>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </>
    );
  }

  if (isDefinitionsError) {
    return (
      <>
        <Divider />
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          Failed to load evidence definitions. Evidence properties cannot be edited.
        </Alert>
      </>
    );
  }

  if (definitionMissing) {
    return (
      <>
        <Divider />
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          Evidence definition not found. Evidence properties cannot be edited.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Divider />
      <Text bold variant="subtitle1">
        Evidence
      </Text>

      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <EvidenceDefinitionSelect
          definitions={definitions}
          selectedDefinitionId={selectedDefinitionId}
          isLoading={isLoadingDefinitions}
          onSelect={onDefinitionChange}
        />
      </Box>

      {selectedDefinition && propertySchema && hasProperties && (
        <Stack spacing={2} sx={{ marginBottom: 2 }}>
          <DynamicSchemaForm
            schema={propertySchema}
            pathPrefix=""
            control={control}
            errors={errors}
          />
        </Stack>
      )}

      {selectedDefinition && propertySchema && !hasProperties && (
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          This definition has no properties to fill in.
        </Text>
      )}
    </>
  );
}
